import { createDefaultBook } from "../../shared/sbkCompat";
import { buildFeedbackPayload } from "../../shared/feedback";
import type { FeedbackPayloadInput } from "../../shared/feedback";
import { runPreflight } from "../../shared/preflight";
import type { Book } from "../../shared/models";
import type { DesktopV2Api } from "../types";
import { readBrowserVersionInfo } from "../utils/appVersion";
import { getWebApiBaseUrl } from "../lib/runtime";
import { cloudAuthDesktopV2 } from "./cloudAuthDesktopV2";
import {
  cacheWebBookDraft,
  clearWebRecovery,
  cloudVirtualPath,
  getOrCreateWebMachineId,
  isCloudVirtualPath,
  loadWebRecovery,
  readWebBookDraft,
  readWebLastSession,
  saveWebRecovery,
  writeWebLastSession,
} from "./webIdbStore";

function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function safeFilename(title: string, ext: string): string {
  const base = (title || "book").replace(/[^\w\s-]+/g, "").trim().slice(0, 80) || "book";
  return `${base}.${ext}`;
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await cloudAuthDesktopV2.getValidAccessToken();
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

const llmAbortControllers = new Map<string, AbortController>();

const RESEARCH_TEXT_PER_ASSET = 12_000;
const RESEARCH_TEXT_TOTAL = 60_000;
const CONTEXT_CHAR_CAP = 120_000;

function slimResearchAssets(raw: unknown): unknown {
  if (!Array.isArray(raw)) return raw;
  let used = 0;
  return raw.map((asset) => {
    if (!asset || typeof asset !== "object") return asset;
    const row = { ...(asset as Record<string, unknown>) };
    const text = typeof row.extractedText === "string" ? row.extractedText : "";
    if (!text) return row;
    const remaining = Math.max(0, RESEARCH_TEXT_TOTAL - used);
    const cap = Math.min(RESEARCH_TEXT_PER_ASSET, remaining);
    row.extractedText = text.length > cap ? `${text.slice(0, cap)}\n…` : text;
    used += row.extractedText.length;
    return row;
  });
}

/** Drop full chapter bodies from the managed-AI proxy payload — context already has the active chapter. */
function slimLlmPayload(payload: unknown): unknown {
  if (!payload || typeof payload !== "object") return payload ?? {};
  const body = { ...(payload as Record<string, unknown>) };
  if (body.bookSnapshot && typeof body.bookSnapshot === "object") {
    const snap = body.bookSnapshot as Record<string, unknown>;
    const chapters = Array.isArray(snap.chapters) ? snap.chapters : [];
    body.bookSnapshot = {
      ...snap,
      chapters: chapters.map((ch) => {
        if (!ch || typeof ch !== "object") return ch;
        const row = ch as Record<string, unknown>;
        return { id: row.id, title: row.title, order: row.order, content: "" };
      }),
      researchAssets: slimResearchAssets(snap.researchAssets),
    };
  }
  if (typeof body.context === "string" && body.context.length > CONTEXT_CHAR_CAP) {
    body.context = body.context.slice(0, CONTEXT_CHAR_CAP);
  }
  return body;
}

function rewriteLlmTransportError(error: unknown): Error {
  const raw = error instanceof Error ? error.message : String(error);
  if (/not signed in|no refresh token/i.test(raw)) {
    return new Error("Please sign in again to use the assistant.");
  }
  if (/failed to fetch|load failed|networkerror/i.test(raw)) {
    return new Error(
      typeof navigator !== "undefined" && navigator.onLine === false
        ? "You're offline. Reconnect and try the assistant again."
        : "Couldn't reach the assistant. Try again in a moment."
    );
  }
  return error instanceof Error ? error : new Error(raw);
}

async function llmProxy(action: string, payload: unknown): Promise<unknown> {
  const requestId =
    payload &&
    typeof payload === "object" &&
    "requestId" in payload &&
    typeof (payload as { requestId?: unknown }).requestId === "string"
      ? (payload as { requestId: string }).requestId
      : "";
  const controller = requestId ? new AbortController() : null;
  if (requestId && controller) {
    llmAbortControllers.get(requestId)?.abort();
    llmAbortControllers.set(requestId, controller);
  }
  try {
    const base = getWebApiBaseUrl();
    let headers: Record<string, string>;
    try {
      headers = await authHeaders();
    } catch (error) {
      throw rewriteLlmTransportError(error);
    }
    const res = await fetch(`${base}/api/desktop/llm/${action}`, {
      method: "POST",
      headers,
      body: JSON.stringify(slimLlmPayload(payload)),
      signal: controller?.signal,
    });
    const text = await res.text();
    let data: unknown = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      data = { error: text };
    }
    if (!res.ok) {
      const err =
        data && typeof data === "object" && "error" in data
          ? String((data as { error: unknown }).error)
          : `LLM request failed (${res.status})`;
      throw new Error(err);
    }
    return data;
  } catch (error) {
    if (controller?.signal.aborted) throw error;
    throw rewriteLlmTransportError(error);
  } finally {
    if (requestId && llmAbortControllers.get(requestId) === controller) {
      llmAbortControllers.delete(requestId);
    }
  }
}

async function exportViaApi(kind: "docx" | "pdf", book: Book): Promise<{ canceled: boolean; filePath?: string }> {
  const base = getWebApiBaseUrl();
  const headers = await authHeaders();
  const res = await fetch(`${base}/api/desktop/export/${kind}`, {
    method: "POST",
    headers,
    body: JSON.stringify({ book }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Export failed (${res.status})`);
  }
  const blob = await res.blob();
  const filename = safeFilename(String(book.title || "book"), kind === "docx" ? "docx" : "pdf");
  downloadBlob(filename, blob);
  return { canceled: false, filePath: filename };
}

function unsupported(feature: string): never {
  throw new Error(`${feature} is not available in the web app. Use Just Write desktop for this feature.`);
}

function createBrowserDesktopV2Api(): DesktopV2Api {
  const noopUnsub = () => undefined;
  let sessionVirtualPath: string | null = null;

  const api: DesktopV2Api = {
    createNewBook: async (options) => {
      const book = createDefaultBook(options);
      sessionVirtualPath = null;
      return { book, filePath: null };
    },
    openBook: async () => {
      unsupported("Opening local .sbk files");
    },
    openBookAtPath: async (filePath) => {
      if (!isCloudVirtualPath(filePath)) unsupported("Opening local project paths");
      const bookId = filePath.slice("cloud://".length).trim();
      if (!bookId) return null;
      const cached = await readWebBookDraft(bookId);
      if (!cached || typeof cached !== "object") return null;
      return { book: cached as Book, filePath };
    },
    // Local IDB only — App orchestrates library push via autoSync so Save can clear
    // "Unsaved" immediately and we avoid double full-book syncs.
    saveBook: async (book, _saveAs) => {
      const id = typeof book.id === "string" ? book.id.trim() : "";
      if (!id) throw new Error("Book has no id — create or open a cloud book first.");
      await cacheWebBookDraft(id, book);
      const path = cloudVirtualPath(id);
      sessionVirtualPath = path;
      await writeWebLastSession({ filePath: path, bookId: id, updatedAt: new Date().toISOString() });
      return { canceled: false, filePath: path, persistedBookId: id };
    },
    saveBookAtPath: async ({ book, filePath }) => {
      const id = typeof book.id === "string" ? book.id.trim() : "";
      if (!id) throw new Error("Book has no id");
      await cacheWebBookDraft(id, book);
      const path = isCloudVirtualPath(filePath) ? filePath : cloudVirtualPath(id);
      sessionVirtualPath = path;
      await writeWebLastSession({ filePath: path, bookId: id, updatedAt: new Date().toISOString() });
      return { filePath: path, persistedBookId: id };
    },
    getLastSession: () => readWebLastSession(),
    getMachineProfile: () => getOrCreateWebMachineId(),
    getLlmUserKeys: async () => ({ hasOpenai: true, hasAnthropic: true }),
    setLlmUserKeys: async () => ({ ok: true }),
    getWebAppUrl: async () => getWebApiBaseUrl(),
    fetchResearchWebpage: async (pageUrl) => {
      const base = getWebApiBaseUrl();
      const headers = await authHeaders();
      const res = await fetch(`${base}/api/desktop/research/fetch`, {
        method: "POST",
        headers,
        body: JSON.stringify({ url: pageUrl }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        title?: string;
        extractedText?: string;
        sourceUrl?: string;
      };
      if (!res.ok) {
        throw new Error(data.error || `Could not fetch webpage (${res.status})`);
      }
      return {
        title: data.title || "",
        extractedText: data.extractedText || "",
        sourceUrl: data.sourceUrl || pageUrl,
      };
    },
    getVersionInfo: async () => readBrowserVersionInfo(),
    pathExists: async () => false,
    chooseDirectory: async () => ({ canceled: true }),
    exportDocx: (book) => exportViaApi("docx", book),
    exportPdf: (params) => {
      const book =
        params && typeof params === "object" && "book" in (params as object)
          ? (params as { book: Book }).book
          : (params as Book);
      return exportViaApi("pdf", book);
    },
    exportPublishPdf: async () => {
      unsupported("Publish PDF export");
    },
    paginatePublish: async () => ({
      pages: [],
      pageCount: 0,
      error: "Print pagination requires the desktop app (pdfkit).",
    }),
    exportCoverWrap: async () => unsupported("Cover wrap export"),
    exportEpub: async () => unsupported("EPUB export"),
    runPreflight: async (params) => {
      const book = params.book as Book;
      return runPreflight({
        title: book.title,
        author: book.author,
        chapters: book.chapters,
        metadata: book.metadata as Record<string, unknown> | null,
        pageCount: params.pageCount,
        hasCoverArt: params.hasCoverArt,
        profile: params.profile as "print" | "ebook" | "package" | undefined,
      });
    },
    exportPublishPackage: async () => unsupported("Publish package export"),
    submitFeedback: async (payload) => {
      const base = getWebApiBaseUrl();
      const headers = await authHeaders().catch(() => ({ "Content-Type": "application/json" }));
      // Normalize the raw modal payload the same way the native handler does so the
      // API receives a numeric rating and structured metadata rather than raw strings.
      const body = buildFeedbackPayload(payload as FeedbackPayloadInput);
      const res = await fetch(`${base}/api/app-feedback`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Feedback failed (${res.status})`);
      return { ok: true };
    },
    // Browser runtime cannot capture the window; the checkbox is a no-op there.
    captureFeedbackScreenshot: async () => ({ error: "Screenshot capture requires the desktop app" }),
    chatComplete: (payload) => llmProxy("chat", payload) as ReturnType<NonNullable<DesktopV2Api["chatComplete"]>>,
    createChapterVariation: (payload) =>
      llmProxy("variation", payload) as ReturnType<DesktopV2Api["createChapterVariation"]>,
    cancelGeneration: async (requestId) => {
      const controller = llmAbortControllers.get(requestId);
      controller?.abort();
      return { canceled: Boolean(controller) };
    },
    translateChapter: (payload) =>
      llmProxy("translate", payload) as ReturnType<DesktopV2Api["translateChapter"]>,
    storyCraftExtract: (payload) =>
      llmProxy("story-craft", payload) as Promise<
        import("../../shared/storyCraftFeedback").DesktopStoryCraftFeedback
      >,
    characterExtract: (payload) => llmProxy("character-extract", payload) as Promise<any>,
    locationExtract: (payload) => llmProxy("location-extract", payload) as Promise<any>,
    timelineExtract: (payload) => llmProxy("timeline-extract", payload) as Promise<any>,
    themeExtract: (payload) => llmProxy("theme-extract", payload) as Promise<any>,
    chapterSummaryGenerate: (payload) => llmProxy("chapter-summary", payload) as Promise<any>,
    loadRecovery: async () => {
      const payload = await loadWebRecovery();
      if (!payload || typeof payload !== "object") return null;
      const row = payload as { book?: Book; filePath?: string | null };
      if (!row.book) return null;
      return { book: row.book, filePath: row.filePath ?? null };
    },
    saveRecovery: async (payload) => {
      await saveWebRecovery(payload);
      return { ok: true };
    },
    clearRecovery: async () => {
      await clearWebRecovery();
      return { ok: true };
    },
    getCloudWebBaseUrl: async () => getWebApiBaseUrl(),
    openExternalUrl: async (url) => {
      window.open(url, "_blank", "noopener,noreferrer");
    },
    cloudRefreshGet: async () => {
      try {
        return localStorage.getItem("jw_desktop_cloud_refresh");
      } catch {
        return null;
      }
    },
    cloudRefreshSet: async (value) => {
      try {
        if (value?.trim()) localStorage.setItem("jw_desktop_cloud_refresh", value.trim());
        else localStorage.removeItem("jw_desktop_cloud_refresh");
      } catch {
        /* ignore */
      }
      return { ok: true };
    },
    cloudHttpRequest: async (request) => {
      const res = await fetch(request.url, {
        method: (request.method || "GET").toUpperCase(),
        headers: request.headers,
        body: request.body,
      });
      return { status: res.status, body: await res.text() };
    },
    generateChapterAudio: async (params) => {
      const base = getWebApiBaseUrl();
      try {
        const res = await fetch(`${base}/api/chapters/${encodeURIComponent(params.chapterId)}/audio/generate`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${params.authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ voiceId: params.voiceId }),
        });
        const data = (await res.json().catch(() => ({}))) as { audio?: unknown; error?: string };
        if (!res.ok) return { ok: false, error: data.error || `HTTP ${res.status}` };
        return { ok: true, audio: data.audio };
      } catch (e) {
        const raw = e instanceof Error ? e.message : String(e);
        const error =
          raw === "Failed to fetch"
            ? "Could not reach the audio API (network, CORS, or the request timed out). Long chapters can take a few minutes — try again, or use the desktop app."
            : raw;
        return { ok: false, error };
      }
    },
    getChapterAudioTtsProvider: async () => {
      const base = getWebApiBaseUrl();
      try {
        const headers = await authHeaders();
        const res = await fetch(`${base}/api/chapters/audio/tts-provider`, { headers });
        const data = (await res.json().catch(() => ({}))) as {
          provider?: "elevenlabs" | "openai" | "local" | null;
          voices?: Array<{ id: string; name: string; provider: "elevenlabs" | "openai" | "local" }>;
          error?: string;
        };
        if (!res.ok) return { ok: false, error: data.error || `HTTP ${res.status}` };
        return { ok: true, provider: data.provider ?? null, voices: data.voices ?? [] };
      } catch (e) {
        return { ok: false, error: e instanceof Error ? e.message : String(e) };
      }
    },
    previewTtsVoice: async () => ({ ok: false, error: "Voice preview is available in the desktop app." }),
    listChapterAudio: async (params) => {
      const base = getWebApiBaseUrl();
      try {
        const res = await fetch(`${base}/api/chapters/${encodeURIComponent(params.chapterId)}/audio`, {
          headers: { Authorization: `Bearer ${params.authToken}` },
        });
        const data = (await res.json().catch(() => ({}))) as { audio?: unknown[]; error?: string };
        if (!res.ok) return { ok: false, error: data.error || `HTTP ${res.status}` };
        return { ok: true, audio: data.audio ?? [] };
      } catch (e) {
        const raw = e instanceof Error ? e.message : String(e);
        return {
          ok: false,
          error:
            raw === "Failed to fetch"
              ? "Could not reach the audio API from the browser. Check your connection and try again."
              : raw,
        };
      }
    },
    narrationLoadEditMap: async () => unsupported("Narrator mode"),
    narrationUpdateEditMap: async () => unsupported("Narrator mode"),
    narrationSaveTake: async () => unsupported("Narrator mode"),
    narrationReadTakeBase64: async () => null,
    narrationDeleteTake: async () => unsupported("Narrator mode"),
    narrationSaveRoomTone: async () => unsupported("Narrator mode"),
    narrationStitch: async () => unsupported("Narrator mode"),
    narrationPublishChapter: async () => unsupported("Narrator mode"),
    narrationUploadExternalFile: async () => unsupported("Narrator mode"),
    narrationExportM4bHint: async () => ({
      ok: false,
      error: "M4B export requires the desktop app.",
    }),
    onRequestSave: () => undefined,
    onRequestExportDocx: () => undefined,
    onRequestExportPdf: () => undefined,
    onOpenFeedback: () => undefined,
    onBookOpened: () => undefined,
    onMenuAction: () => noopUnsub,
    onGoogleOAuthCallback: () => noopUnsub,
    googleGetCredentials: async () => unsupported("Google credentials"),
    googleExchangeToken: async () => unsupported("Google OAuth"),
    googleRefreshToken: async () => unsupported("Google OAuth"),
    googleApiGet: async () => unsupported("Google API"),
    dbFindOrCreateUserByGoogle: async () => unsupported("Legacy DB bridge"),
    syncBookToDatabase: async () => unsupported("Legacy DB bridge"),
    dbBookExists: async () => false,
    dbGetBookUpdatedAt: async () => null,
    dbGetChapterTimestamps: async () => ({}),
    dbLoadBook: async () => null,
    dbListBooks: async () => [],
  };

  return api;
}

/** Install `window.desktopV2Api` for browser SPA. No-op inside Electron. */
export function installBrowserDesktopV2Api(): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as {
    desktopV2Api?: DesktopV2Api;
    __JUSTWRITE_RUNTIME__?: "electron" | "web";
  };

  // Already classified as web — reinstall if the stub is missing or incomplete (HMR).
  if (w.__JUSTWRITE_RUNTIME__ === "web") {
    const api = w.desktopV2Api as DesktopV2Api | undefined;
    if (!api || typeof api.getCloudWebBaseUrl !== "function") {
      w.desktopV2Api = createBrowserDesktopV2Api();
    }
    return;
  }
  if (w.__JUSTWRITE_RUNTIME__ === "electron") return;

  // Electron preload exposes the API before any module runs (no marker yet).
  // Detect Electron via userAgent — never treat a Chromium browser tab as Electron.
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isElectronUa = /\bElectron\//.test(ua);
  if (isElectronUa && typeof w.desktopV2Api !== "undefined") {
    w.__JUSTWRITE_RUNTIME__ = "electron";
    return;
  }

  w.__JUSTWRITE_RUNTIME__ = "web";
  w.desktopV2Api = createBrowserDesktopV2Api();
}

// Side-effect: install before App mounts when this module is imported first.
installBrowserDesktopV2Api();
